import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
  type MotionValue,
} from "framer-motion";

import ProjectCover from "@/components/ProjectCover";
import { useMediaQuery } from "@/lib/media";
import { scrollToSectionIndex, useSectionProgress } from "@/lib/scroll";
import type { Project } from "@/types/projects";

const GEOMETRY = {
  wide: {
    plane: 0.35,
    stepX: 0.175,
    stepY: -0.06,
    stepZ: -0.21,
    lift: -0.015,
    rotate: -32,
    perspective: 1.8,
  },
  compact: {
    plane: 0.72,
    stepX: 0.34,
    stepY: -0.12,
    stepZ: -0.42,
    lift: -0.02,
    rotate: -20,
    perspective: 2.6,
  },
};

const COMPACT_BREAKPOINT = 768;

const DECK_ASPECT = 1.9;

const ACTIVE_BRIGHTNESS = 1.08;

const LIFT_FALLOFF = 0.9;

function smoothstep(t: number) {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

const FADE_BEHIND = 1.5;
const FADE_AHEAD = 5.5;
const FADE_SPAN = 1.5;

const FADE_BEHIND_COMPACT = 0.18;
const FADE_SPAN_BEHIND_COMPACT = 0.82;
const FADE_AHEAD_COMPACT = 3;

const RUNWAY_PER_PROJECT = 52;

const MAX_RUNWAY = 340;

const VELOCITY_RANGE = 3200;

const VELOCITY_OFFSET = 0.3;

const HOVER_VELOCITY_LIMIT = 900;

const DRAG_TOLERANCE = 12;

const TOUCH_INERTIA = {
  timeConstant: 325,
  power: 0.8,
};

const HOVER_SHIFT = 0.55;
const HOVER_SCALE = 0.04;
const HOVER_LIFT = 0.25;

const TAP_SHIFT = 0;
const TAP_LIFT = 1.15;

type PlaneProps = {
  project: Project;
  index: number;
  position: MotionValue<number>;
  stepX: MotionValue<number>;
  stepY: MotionValue<number>;
  stepZ: MotionValue<number>;
  lift: MotionValue<number>;
  planeWidth: number;
  rotate: number;
  compact: boolean;
  active: boolean;
  highlighted: boolean;
  forcedHover: boolean;
  pointerHover: boolean;
  onHoverChange: (index: number, hovered: boolean) => void;
  onFocus: (index: number) => void;
  onActivate: (index: number) => void;
};

const Plane = memo(function Plane({
  project,
  index,
  position,
  stepX,
  stepY,
  stepZ,
  lift,
  planeWidth,
  rotate,
  compact,
  active,
  highlighted,
  forcedHover,
  pointerHover,
  onHoverChange,
  onFocus,
  onActivate,
}: PlaneProps) {
  const offset = useTransform(position, (p) => index - p);

  const hoverTarget = useMotionValue(0);
  const hover = useSpring(hoverTarget, {
    stiffness: 260,
    damping: 30,
    mass: 0.3,
  });

  useEffect(() => {
    if (compact) hoverTarget.set(forcedHover ? 1 : 0);
  }, [compact, forcedHover, hoverTarget]);

  useEffect(() => {
    if (!compact) hoverTarget.set(pointerHover ? 1 : 0);
  }, [compact, pointerHover, hoverTarget]);

  const shift = compact ? TAP_SHIFT : HOVER_SHIFT;
  const liftFactor = compact ? TAP_LIFT : HOVER_LIFT;

  const x = useTransform(
    [offset, stepX, hover] as MotionValue<number>[],
    ([o, step, h]: number[]) => o * step + h * step * shift,
  );
  const y = useTransform(
    [offset, stepY, lift, hover] as MotionValue<number>[],
    ([o, step, up, h]: number[]) =>
      o * step +
      h * step * shift +
      up * smoothstep(1 - Math.abs(o) / LIFT_FALLOFF),
  );
  const z = useTransform(
    [offset, stepZ, hover] as MotionValue<number>[],
    ([o, step, h]: number[]) => o * step + h * Math.abs(step) * liftFactor,
  );

  const fadeBehind = compact ? FADE_BEHIND_COMPACT : FADE_BEHIND;
  const fadeSpanBehind = compact ? FADE_SPAN_BEHIND_COMPACT : FADE_SPAN;
  const fadeAhead = compact ? FADE_AHEAD_COMPACT : FADE_AHEAD;

  const opacity = useTransform(offset, (o) => {
    if (o < -fadeBehind) {
      const linear = Math.max(0, 1 + (o + fadeBehind) / fadeSpanBehind);
      return compact ? smoothstep(linear) : linear;
    }
    if (o > fadeAhead) return Math.max(0, 1 - (o - fadeAhead) / FADE_SPAN);
    return 1;
  });

  const scale = useTransform(hover, (h) => 1 + h * HOVER_SCALE);

  const zIndex = useTransform(
    [offset, hover] as MotionValue<number>[],
    ([o, h]: number[]) =>
      h > 0.5 ? 200 : Math.abs(o) < 0.5 ? 100 : Math.round(50 - Math.abs(o)),
  );

  const filter = `brightness(${active ? ACTIVE_BRIGHTNESS : 1})`;

  const xRest = useTransform(
    [offset, stepX] as MotionValue<number>[],
    ([o, step]: number[]) => o * step,
  );
  const yRest = useTransform(
    [offset, stepY, lift] as MotionValue<number>[],
    ([o, step, up]: number[]) =>
      o * step + up * smoothstep(1 - Math.abs(o) / LIFT_FALLOFF),
  );
  const zRest = useTransform(
    [offset, stepZ] as MotionValue<number>[],
    ([o, step]: number[]) => o * step,
  );

  const zIndexRest = useTransform(offset, (o) =>
    Math.abs(o) < 0.5 ? 100 : Math.round(50 - Math.abs(o)),
  );

  const pointerEvents = useTransform(opacity, (o) =>
    o > 0.15 ? "auto" : "none",
  );

  const visibility = useTransform(opacity, (o) =>
    o > 0.01 ? "visible" : "hidden",
  );

  const press = useRef<{ x: number; y: number; time: number } | null>(null);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    press.current = { x: event.clientX, y: event.clientY, time: Date.now() };

    if (event.pointerType === "mouse") {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = press.current;
    press.current = null;
    if (!start) return;

    const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (moved < DRAG_TOLERANCE && Date.now() - start.time < 700)
      onActivate(index);
  };

  const handlePointerCancel = () => {
    press.current = null;
  };

  const pointerHandlers = {
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
  };

  return (
    <>
      {!compact && (
        <motion.div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: `${planeWidth * 100}%`,
            zIndex: zIndexRest,
            transformStyle: "preserve-3d",
          }}
        >
          <motion.div
            data-plane-index={index}
            aria-hidden="true"
            className="w-full aspect-[3/2] cursor-pointer"
            style={{
              x: xRest,
              y: yRest,
              z: zRest,
              rotateY: rotate,
              visibility,
              pointerEvents,
            }}
            {...pointerHandlers}
          />
        </motion.div>
      )}

      <motion.div
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          width: `${planeWidth * 100}%`,
          zIndex,
          transformStyle: "preserve-3d",
        }}
      >
        <motion.div
          className="relative w-full"
          style={{
            x,
            y,
            z,
            scale,
            rotateY: rotate,
            opacity,
            visibility,

            pointerEvents: compact ? pointerEvents : "none",
            transformStyle: "preserve-3d",

            ...(compact ? null : { filter }),
          }}
          {...(compact
            ? {
                ...pointerHandlers,
                onMouseEnter: () => {
                  hoverTarget.set(1);
                  onHoverChange(index, true);
                },
                onMouseLeave: () => {
                  hoverTarget.set(0);
                  onHoverChange(index, false);
                },
              }
            : null)}
        >
          <Link
            to={`/portfolio/${project.slug}`}
            onFocus={() => {
              onHoverChange(index, true);
              onFocus(index);
            }}
            onBlur={() => onHoverChange(index, false)}

            onClick={(event) => {
              if (event.detail !== 0) event.preventDefault();
            }}
            className="block cursor-pointer outline-offset-4"
          >
            <div className="w-full aspect-[3/2]">
              <ProjectCover project={project} priority={index === 0} />
            </div>
          </Link>

          <div
            className={`pointer-events-none absolute flex items-center whitespace-nowrap transition-opacity duration-200 ${
              compact
                ? "top-full left-0 mt-3 gap-3"
                : "top-[62%] left-full pl-4 gap-4"
            } ${highlighted ? "opacity-100" : "opacity-0"}`}
          >
            <span className={`h-px bg-ink ${compact ? "w-8" : "w-28"}`} />
            <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-ink">
              {project.title}
            </span>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
});

export default function ProjectsDeck({ projects }: { projects: Project[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const lastIndex = projects.length - 1;

  const runway = Math.min(projects.length * RUNWAY_PER_PROJECT, MAX_RUNWAY);

  const [current, setCurrent] = useState(0);
  const [wakeTick, setWakeTick] = useState(0);
  useEffect(() => {
    setWakeTick((t) => t + 1);
  }, []);
  const [hoveredPlane, setHoveredPlane] = useState<number | null>(null);
  const [compact, setCompact] = useState(
    () => window.innerWidth < COMPACT_BREAKPOINT,
  );
  const [stageWidth, setStageWidth] = useState(0);

  const [planeScale, setPlaneScale] = useState(1);

  const stepX = useMotionValue(0);
  const stepY = useMotionValue(0);
  const stepZ = useMotionValue(0);
  const lift = useMotionValue(0);

  const geometry = compact ? GEOMETRY.compact : GEOMETRY.wide;

  const progress = useSectionProgress(sectionRef);
  const base = useTransform(progress, (p) => p * lastIndex);

  const { scrollY } = useScroll();
  const velocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(velocity, {
    stiffness: 120,
    damping: 32,
    mass: 0.5,
  });

  const offsetByVelocity = useTransform(smoothVelocity, (v) => {
    const normalized = Math.max(-1, Math.min(1, v / VELOCITY_RANGE));
    return normalized * VELOCITY_OFFSET;
  });

  const scrollPosition = useTransform(
    [base, offsetByVelocity] as MotionValue<number>[],
    ([b, extra]: number[]) =>
      Math.min(lastIndex + 0.4, Math.max(-0.4, b + extra)),
  );

  const smoothScroll = useSpring(scrollPosition, {
    stiffness: 140,
    damping: 28,
    mass: 0.4,
  });

  const touchPosition = useMotionValue(0);

  const position = useTransform(
    [smoothScroll, touchPosition] as MotionValue<number>[],
    ([byScroll, byTouch]: number[]) => (compact ? byTouch : byScroll),
  );

  useMotionValueEvent(base, "change", (p) => {
    if (!compact) setCurrent(Math.min(lastIndex, Math.max(0, Math.round(p))));
  });

  useMotionValueEvent(touchPosition, "change", (p) => {
    if (compact) setCurrent(Math.min(lastIndex, Math.max(0, Math.round(p))));
  });

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const apply = (value: MotionValue<number>, next: number) => {
      value.set(value.get() === next ? next + 1e-6 : next);
    };

    const measure = () => {
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      const isCompact = w < COMPACT_BREAKPOINT;
      const config = isCompact ? GEOMETRY.compact : GEOMETRY.wide;

      const fit = h > 0 ? Math.min(1, (h * DECK_ASPECT) / w) : 1;
      const scaled = w * fit;

      apply(stepX, scaled * config.stepX);
      apply(stepY, scaled * config.stepY);
      apply(stepZ, scaled * config.stepZ);
      apply(lift, scaled * config.lift);

      setCompact(isCompact);
      setStageWidth(scaled);
      setPlaneScale(fit);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [stepX, stepY, stepZ, lift]);

  const shortViewport = useMediaQuery("(max-height: 639px)");
  const lowViewport = useMediaQuery("(max-height: 719px)");
  const shortStage = compact && shortViewport;
  const shortWide = !compact && lowViewport;

  const shortLayout = shortStage || shortWide;

  const compactRef = useRef(compact);
  compactRef.current = compact;
  const hoveredRef = useRef(hoveredPlane);
  hoveredRef.current = hoveredPlane;

  const glideRef = useRef<ReturnType<typeof animate> | null>(null);
  const glideTo = useCallback(
    (index: number) => {
      glideRef.current?.stop();
      glideRef.current = animate(touchPosition, index, {
        type: "spring",
        stiffness: 200,
        damping: 30,
      });
    },
    [touchPosition],
  );

  const pointer = useRef<{ x: number; y: number } | null>(null);

  const hitTest = useCallback(() => {
    if (compactRef.current) return;
    const at = pointer.current;
    if (!at) return;

    if (Math.abs(smoothVelocity.get()) > HOVER_VELOCITY_LIMIT) {
      setHoveredPlane(null);
      return;
    }

    const target = document
      .elementFromPoint(at.x, at.y)
      ?.closest("[data-plane-index]");

    const next = target
      ? Number((target as HTMLElement).dataset.planeIndex)
      : null;

    setHoveredPlane((previous) => (previous === next ? previous : next));
  }, [smoothVelocity]);

  useMotionValueEvent(position, "change", hitTest);

  const handleHoverChange = useCallback((index: number, hovered: boolean) => {
    setHoveredPlane((previous) =>
      hovered ? index : previous === index ? null : previous,
    );
  }, []);

  const handleFocus = useCallback(
    (index: number) => {
      if (compactRef.current) glideTo(index);
      else scrollToSectionIndex(sectionRef.current, index, lastIndex);
    },
    [lastIndex, glideTo],
  );

  const handleActivate = useCallback(
    (index: number) => {
      if (compactRef.current && hoveredRef.current !== index) {
        setHoveredPlane(index);
        glideTo(index);
        return;
      }
      navigate(`/portfolio/${projects[index].slug}`);
    },
    [navigate, projects, glideTo],
  );

  const dragRef = useRef<{
    id: number;
    startX: number;
    startPos: number;
    lastX: number;
    lastT: number;
    velocity: number;
    moved: boolean;
  } | null>(null);

  const handleStagePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!compactRef.current) return;
    glideRef.current?.stop();
    dragRef.current = {
      id: e.pointerId,
      startX: e.clientX,
      startPos: touchPosition.get(),
      lastX: e.clientX,
      lastT: e.timeStamp,
      velocity: 0,
      moved: false,
    };
  };

  const handleStagePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!compactRef.current && e.pointerType === "mouse") {
      pointer.current = { x: e.clientX, y: e.clientY };
      hitTest();
    }

    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.id) return;

    const step = stepX.get() || 1;
    const dx = e.clientX - drag.startX;

    if (!drag.moved && Math.abs(dx) > DRAG_TOLERANCE) {
      drag.moved = true;
      setHoveredPlane(null);
    }

    const next = Math.min(
      lastIndex + 0.4,
      Math.max(-0.4, drag.startPos - dx / step),
    );
    touchPosition.set(next);

    const dt = e.timeStamp - drag.lastT;
    if (dt > 0) {
      const instant = (-(e.clientX - drag.lastX) / step) * (1000 / dt);
      drag.velocity = drag.velocity * 0.6 + instant * 0.4;
    }
    drag.lastX = e.clientX;
    drag.lastT = e.timeStamp;
  };

  const handleStagePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.id) return;
    dragRef.current = null;
    if (!drag.moved) return;

    const projected =
      touchPosition.get() +
      drag.velocity * (TOUCH_INERTIA.timeConstant / 1000) * TOUCH_INERTIA.power;
    const target = Math.min(lastIndex, Math.max(0, Math.round(projected)));
    glideRef.current = animate(touchPosition, target, {
      type: "spring",
      stiffness: 170,
      damping: 26,
      velocity: drag.velocity,
    });
  };

  const handleStagePointerLeave = () => {
    if (compactRef.current) return;
    pointer.current = null;
    setHoveredPlane(null);
  };

  const introBlock = (
    <div
      className={`flex flex-col max-w-md lg:max-w-lg ${
        shortLayout ? "gap-2" : "gap-3"
      }`}
    >
      <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-brand dark:text-[#DADADA]">
        {t("common.portfolio")}
      </p>

      <h2
        className={`font-medium leading-tight text-ink ${
          shortLayout ? "text-2xl md:text-3xl" : "text-3xl md:text-5xl"
        }`}
      >
        {t("portfolio.introPrefix")}
      </h2>

      <p
        className={`hidden md:block font-light text-ink-muted ${
          shortLayout ? "text-sm" : "text-base md:text-lg"
        }`}
      >
        {t("portfolio.intro")}
      </p>
    </div>
  );

  const hintBlock = (
    <div className="flex items-end justify-between gap-6">
      <p className="font-mono text-[11px] md:text-xs tracking-[0.2em] text-ink-subtle uppercase ml-auto">
        {t(compact ? "portfolio.dragHint" : "portfolio.surfHint")}
      </p>
    </div>
  );

  const stage = (
    <div
      ref={stageRef}
      className={
        shortLayout
          ? "relative flex-1 min-h-0 w-full overflow-hidden"
          : "page-shell sticky top-0 h-svh overflow-hidden"
      }
      style={{
        perspective: `${stageWidth * geometry.perspective}px`,
        touchAction: compact ? "pan-y" : "auto",
      }}
      onPointerDown={handleStagePointerDown}
      onPointerMove={handleStagePointerMove}
      onPointerUp={handleStagePointerEnd}
      onPointerCancel={handleStagePointerEnd}
      onPointerLeave={handleStagePointerLeave}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ transformStyle: "preserve-3d" }}
      >
        {projects.map((project, index) => (
          <Plane
            key={`${project.slug}:${wakeTick}`}
            project={project}
            index={index}
            position={position}
            stepX={stepX}
            stepY={stepY}
            stepZ={stepZ}
            lift={lift}
            planeWidth={geometry.plane * planeScale}
            rotate={geometry.rotate}
            compact={compact}

            highlighted={
              hoveredPlane === null ? current === index : hoveredPlane === index
            }
            active={current === index}
            forcedHover={compact && hoveredPlane === index}
            pointerHover={!compact && hoveredPlane === index}
            onHoverChange={handleHoverChange}
            onFocus={handleFocus}
            onActivate={handleActivate}
          />
        ))}
      </div>

      {!shortLayout && (
        <div className="pointer-events-none absolute inset-0 flex flex-col justify-between px-4 md:px-8 xl:px-16 pt-24 md:pt-32 pb-4 md:pb-8 xl:pb-10">
          {introBlock}
          {hintBlock}
        </div>
      )}
    </div>
  );

  if (shortLayout) {
    return (
      <section
        ref={sectionRef}
        className="relative w-full"
        style={{ height: compact ? "100svh" : `${100 + runway}vh` }}
        aria-label={t("common.portfolio")}
      >
        <div className="page-shell sticky top-0 h-svh flex flex-col overflow-hidden">
          <div className="shrink-0 px-4 md:px-8 xl:px-16 pt-20 pb-3">
            {introBlock}
          </div>

          {stage}

          <div className="shrink-0 px-4 md:px-8 xl:px-16 pb-3">{hintBlock}</div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      style={{ height: compact ? "100svh" : `${100 + runway}vh` }}
      aria-label={t("common.portfolio")}
    >
      {stage}
    </section>
  );
}
